import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { ClipboardText, Receipt, CurrencyEur, CaretRight, CheckCircle, BuildingApartment } from "@phosphor-icons/react";
import api from "../lib/api";
import { eur0 as eur } from "../lib/impresa";


const LIVELLO = {
  scaduto: "bg-red-50 text-red-700 border-red-200",
  oggi: "bg-red-50 text-red-700 border-red-200",
  critico: "bg-orange-50 text-orange-700 border-orange-200",
  prossimo: "bg-slate-50 text-slate-600 border-slate-200",
  attenzione: "bg-amber-50 text-amber-800 border-amber-200",
  recente: "bg-slate-50 text-slate-600 border-slate-200",
  aperto: "bg-slate-50 text-slate-600 border-slate-200",
};

function Colonna({ testid, eyebrow, titolo, icon: Icon, iconClass, conta, sotto, link, linkLabel, items, renderRow, vuoto }) {
  return (
    <Card className="p-0 border-slate-200 shadow-none overflow-hidden flex flex-col" data-testid={testid}>
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500 truncate">{eyebrow}</div>
          <Link
            to={link}
            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 shrink-0"
            data-testid={`${testid}-link`}
          >
            {linkLabel}
          </Link>
        </div>
        <div className="flex items-center gap-2.5 mt-2">
          <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${iconClass}`}>
            <Icon size={16} />
          </div>
          <h3 className="font-heading font-semibold text-sm leading-snug">{titolo}</h3>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-slate-100 flex items-baseline gap-2">
        <span className="font-heading text-2xl font-bold tabular-nums text-slate-900 leading-none" data-testid={`${testid}-count`}>
          {conta}
        </span>
        <span className="text-xs text-slate-500 leading-tight">{sotto}</span>
      </div>

      <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto flex-1">
        {items.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500 italic flex flex-col items-center gap-1.5">
            <CheckCircle size={18} className="text-emerald-600" />
            {vuoto}
          </div>
        ) : (
          items.map(renderRow)
        )}
      </div>
    </Card>
  );
}

function Riga({ to, testid, titolo, sotto, badge, livello }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">{titolo}</div>
        <div className="text-[11px] font-mono text-slate-500 truncate mt-0.5">{sotto}</div>
      </div>
      <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${LIVELLO[livello] || LIVELLO.aperto}`}>
        {badge}
      </span>
      <CaretRight size={12} className="text-slate-300 group-hover:text-slate-900 shrink-0" />
    </Link>
  );
}

export default function CruscottoScadenze() {
  const [data, setData] = useState(null);
  const [cond, setCond] = useState(null);

  useEffect(() => {
    api.get("/dashboard/cruscotto-scadenze").then((r) => setData(r.data)).catch(() => {});
    api.get("/condomini/scadenze/tutte?giorni=30").then((r) => setCond(r.data)).catch(() => setCond([]));
  }, []);

  if (!data) return null;

  const inc = data.incarichi || { totale: 0, scaduti: 0, items: [] };
  const prev = data.preventivi || { totale: 0, importo_totale: 0, items: [] };
  const fat = data.fatture || { totale: 0, scadute: 0, importo_totale: 0, importo_scaduto: 0, items: [] };

  return (
    <div className="mb-6" data-testid="cruscotto-scadenze">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Cruscotto scadenze</div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-3 ${cond && cond.length > 0 ? "xl:grid-cols-4" : ""} gap-4`}>
        {cond && cond.length > 0 && (
          <Colonna
            testid="cruscotto-condomini"
            eyebrow="Entro 30 giorni"
            titolo="Adempimenti condomini"
            icon={BuildingApartment}
            iconClass="bg-amber-50 text-amber-700"
            conta={cond.length}
            sotto={cond.filter((c) => c.livello === "scaduto").length > 0 ? `di cui ${cond.filter((c) => c.livello === "scaduto").length} già scaduti` : "nessuno scaduto"}
            link="/condomini"
            linkLabel="Condomini"
            items={cond.slice(0, 8)}
            vuoto="Nessun adempimento vicino"
            renderRow={(it, i) => (
              <Riga
                key={`${it.condominio_id}-${it.codice}`}
                to={`/condomini/${it.condominio_id}?tab=adempimenti`}
                testid={`cruscotto-condominio-${it.condominio_id}-${it.codice}`}
                titolo={(it.condominio || "—").toUpperCase()}
                sotto={`${it.titolo} · ${new Date(it.scadenza).toLocaleDateString("it-IT")}`}
                livello={it.livello === "critico" ? "critico" : it.livello === "scaduto" ? "scaduto" : it.livello === "oggi" ? "oggi" : "prossimo"}
                badge={it.giorni < 0 ? `${Math.abs(it.giorni)}gg fa` : it.giorni === 0 ? "Oggi" : `Fra ${it.giorni}gg`}
              />
            )}
          />
        )}
        <Colonna
          testid="cruscotto-incarichi"
          eyebrow="Entro 30 giorni"
          titolo="Incarichi in scadenza"
          icon={ClipboardText}
          iconClass="bg-red-50 text-red-700"
          conta={inc.totale}
          sotto={inc.scaduti > 0 ? `di cui ${inc.scaduti} già scadut${inc.scaduti === 1 ? "o" : "i"}` : "nessuno scaduto"}
          link="/pratiche"
          linkLabel="Incarichi"
          items={inc.items}
          vuoto="Nessuna scadenza vicina"
          renderRow={(it) => (
            <Riga
              key={it.id}
              to={it.url}
              testid={`cruscotto-incarico-${it.id}`}
              titolo={(it.cliente_nome || it.titolo || "—").toUpperCase()}
              sotto={`${it.titolo || ""} · ${new Date(it.scadenza).toLocaleDateString("it-IT")}`}
              livello={it.livello}
              badge={
                it.giorni < 0
                  ? `${Math.abs(it.giorni)}gg fa`
                  : it.giorni === 0
                  ? "Oggi"
                  : `Fra ${it.giorni}gg`
              }
            />
          )}
        />

        <Colonna
          testid="cruscotto-preventivi"
          eyebrow="In attesa di risposta"
          titolo="Preventivi inviati"
          icon={Receipt}
          iconClass="bg-blue-50 text-blue-700"
          conta={prev.totale}
          sotto={prev.totale > 0 ? `per ${eur(prev.importo_totale)} in gioco` : "nessuno in attesa"}
          link="/preventivi"
          linkLabel="Preventivi"
          items={prev.items}
          vuoto="Nessun preventivo in attesa"
          renderRow={(it) => (
            <Riga
              key={it.id}
              to={it.url}
              testid={`cruscotto-preventivo-${it.id}`}
              titolo={(it.cliente_nome || `Preventivo ${it.numero}`).toUpperCase()}
              sotto={`N. ${it.numero} · ${eur(it.importo)}`}
              livello={it.livello}
              badge={it.giorni <= 0 ? "Oggi" : `${it.giorni}gg`}
            />
          )}
        />

        <Colonna
          testid="cruscotto-fatture"
          eyebrow="Da incassare"
          titolo="Avvisi e fatture aperte"
          icon={CurrencyEur}
          iconClass="bg-amber-50 text-amber-700"
          conta={eur(fat.importo_totale)}
          sotto={
            fat.scadute > 0
              ? `${fat.scadute} scadut${fat.scadute === 1 ? "a" : "e"} · ${eur(fat.importo_scaduto)}`
              : `${fat.totale} apert${fat.totale === 1 ? "a" : "e"}, nessuna scaduta`
          }
          link="/fatturazione"
          linkLabel="Fatturazione"
          items={fat.items}
          vuoto="Tutto incassato"
          renderRow={(it) => (
            <Riga
              key={it.id}
              to={it.url}
              testid={`cruscotto-fattura-${it.id}`}
              titolo={(it.cliente_nome || `Avviso ${it.numero}`).toUpperCase()}
              sotto={`N. ${it.numero} · ${eur(it.totale)}${it.scadenza ? ` · scad. ${new Date(it.scadenza).toLocaleDateString("it-IT")}` : ""}`}
              livello={it.livello}
              badge={
                it.giorni === null || it.giorni === undefined
                  ? "Senza scad."
                  : it.giorni < 0
                  ? `${Math.abs(it.giorni)}gg fa`
                  : it.giorni === 0
                  ? "Oggi"
                  : `Fra ${it.giorni}gg`
              }
            />
          )}
        />
      </div>
    </div>
  );
}
